import type { ContactOriginActor } from "@/features/contact/types";

import { AI_ASSISTANT } from "@/config/constants";

/**
 * Two deliberately different sets live here. `ORIGINS` is the whole
 * `contacts.origin` enum - what may be *displayed and filtered on*.
 * `DECLARABLE_ORIGINS` is the subset a staff member may *claim*; the rest
 * is stamped by the write path that produced the contact, so offering any of
 * them on a form would be a claim the person making it cannot back.
 */
export const ORIGINS = [
  "ad",
  "organic",
  "referral",
  "manual",
  "api",
  "import",
  "crm",
] as const;

export type ContactOrigin = (typeof ORIGINS)[number];

/** Who made a change when no teammate did, named as the activity timeline names them. */
export const TOUCH_ACTOR_LABEL: Record<
  Exclude<ContactOriginActor, "user">,
  string
> = {
  ai: AI_ASSISTANT.name,
  api: "API",
  system: "System",
};

const ORIGIN_LABEL: Record<ContactOrigin, string> = {
  ad: "Ad",
  organic: "Organic",
  referral: "Referral",
  manual: "Manual",
  api: "API",
  import: "Import",
  crm: "EMR",
};

export const ORIGIN_ICON: Record<ContactOrigin, string> = {
  ad: "solar:cursor-linear",
  organic: "solar:global-linear",
  referral: "solar:users-group-two-rounded-linear",
  manual: "solar:pen-new-square-linear",
  api: "solar:code-square-linear",
  import: "solar:import-linear",
  crm: "solar:stethoscope-linear",
};

/** The drawer header's one-line hint: "<verb> <date>, <channel>". */
export const ORIGIN_HINT_VERB: Record<ContactOrigin, string> = {
  ad: "From an ad",
  organic: "Wrote in",
  referral: "Referred",
  manual: "Added",
  api: "Created by API",
  import: "Imported",
  crm: "Synced",
};

export const ORIGIN_OPTIONS = ORIGINS.map((value) => ({
  value,
  label: ORIGIN_LABEL[value],
}));

export const getOriginLabel = (
  value: string | null | undefined,
): string | null =>
  value ? (ORIGIN_LABEL[value as ContactOrigin] ?? value) : null;

/**
 * A curated subset, never `ORIGINS` filtered at runtime. `CreateContactDto`
 * accepts any enum member, so the list here is the only thing keeping a
 * machine-set origin off a form: `ad`, `organic` and `api` are stamped by the
 * write path that produced the contact, and `import` belongs to the CSV path,
 * which stamps it itself.
 */
export const DECLARABLE_ORIGINS = [
  "manual",
  "referral",
  "crm",
] as const satisfies readonly ContactOrigin[];

export type DeclarableOrigin = (typeof DECLARABLE_ORIGINS)[number];

export const DECLARABLE_ORIGIN_OPTIONS = DECLARABLE_ORIGINS.map((value) => ({
  value,
  label: ORIGIN_LABEL[value],
}));

/** A lead with nothing booked or ordered is Converted later when its person books or orders this soon after; the reports use the same window. */
export const CONVERTED_LATER_DAYS = 90;
